// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Video from "@flip/App/Components/YoutubeVideo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import styled, { css } from "styled-components";

export const enum fields {
    src = "src",
    fullWidth = "fullWidth",
    autoPlay = "autoPlay",
}
interface VideoWidgetProps extends WidgetProps {
    fields: {
        [fields.src]: string;
        [fields.autoPlay]: boolean;
        [fields.fullWidth]: boolean;
    };
}
type Props = VideoWidgetProps;
interface VideoWidgetStyles {
    container?: InjectableCss;
    videoContainer?: InjectableCss;
    rightSection?: InjectableCss;
    videoSection?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
}
export const videoWidgetStyles: VideoWidgetStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    videoContainer: {
        css: css`
            display: flex;
            justify-content: center;
            overflow: hidden;
            position: relative;

            ${({ fullWidth }: any) =>
                css`
                    width: ${fullWidth ? "100%" : "50%"};
                    padding-top: ${fullWidth ? "50%" : 0};
                `}

            iframe {
                border: 0;
                height: 100%;
                left: 0;
                position: absolute;
                top: 0;
                width: 100%;
            }
        `,
    },
    videoSection: {
        css: css`
            padding: 15px;
            flex: 1 1 400px;
            display: flex;
            justify-content: center;
        `,
    },
};

const styles = videoWidgetStyles;

const VideoWidget: React.FC<Props> = ({ fields: { autoPlay, fullWidth, src } }) => {
    return (
        <StyledWrapper {...styles.container} data-test-selector="video">
            <StyledWrapper {...styles.videoSection}>
                <StyledWrapper {...styles.videoContainer} {...{ fullWidth }}>
                    <Video src={src} autoPlay={autoPlay} />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};
export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            displayName: "Video Src",
            name: fields.src,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            displayName: "Is Full Width",
            name: fields.fullWidth,
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            displayName: "Auto Play",
            name: fields.autoPlay,
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
    ],
};
const widgetModule: WidgetModule = {
    component: VideoWidget,
    definition,
};
export default widgetModule;
